import React, { useState, useMemo, useEffect } from 'react';
import { TextField, Switch, FormControlLabel, Button } from '@material-ui/core';
import { useQuery } from 'react-query';
import { Autocomplete } from '@material-ui/lab';
import { useAlerts } from 'hooks/alerts-hooks';
import client from '../http/axios-client';

const ChangePermissions = () => {
  const {
    data: roles,
    isLoading: rolesLoading,
    refetch: refetchRoles,
  } = useQuery(
    [ 'roles', 'all' ], () => {
      return client.get('/manage-permissions/users-roles', {
        params: {
          _limit: -1,
        },
      });
    },
  );

  const {
    data: permissions,
    isLoading: permissionsLoading,
    refetch: refetchPermissions,
  } = useQuery(
    [ 'permissions', 'all' ], () => {
      return client.get('/manage-permissions', {
        params: {
          _limit: -1,
        },
      });
    },
  );

  const { alertSuccess, alertError } = useAlerts();

  const [ selectedRole, setSelectedRole ] = useState('');
  const [ selectedController, setSelectedController ] = useState('');
  const [ selectedAction, setSelectedAction ] = useState('');
  const [ enabled, setEnabled ] = useState(false);
  const [ foundPermissionId, setFoundPermissionId ] = useState(null);

  const [ requestLoading, setRequestLoading ] = useState(false);

  const controllers = useMemo(() => {
    if (permissionsLoading || rolesLoading || '' === selectedRole) {
      return [];
    }

    const controllersWithDuplicates = permissions
      .map((per) => {
        return per.controller;
      });

    return [ ...new Set(controllersWithDuplicates) ];
  }, [ permissions, permissionsLoading, roles, rolesLoading, selectedRole ]);

  const actions = useMemo(() => {
    if (permissionsLoading || rolesLoading || '' === selectedRole || 0 === controllers.length) {
      return [];
    }

    const actionsWithDuplicates = permissions
      .map((per) => {
        return per.action;
      });

    return [ '__all__', ...new Set(actionsWithDuplicates) ];
  }, [ permissions, permissionsLoading, roles, rolesLoading, selectedRole, controllers ]);

  const isSwitchEnabled = useMemo(() => {
    return null != selectedAction && '' !== selectedAction && null != selectedController && '' !== selectedController && null != selectedRole && '' !== selectedRole;
  }, [ selectedAction, selectedController, selectedRole ]);

  useEffect(() => {
    if (isSwitchEnabled) {
      const foundPermission = permissions.find((per) => {
        return per.action === selectedAction && per.controller === selectedController && per?.role?.id === selectedRole?.id;
      });

      if (foundPermission === undefined) {
        setEnabled(null);
      } else {
        setEnabled(foundPermission.enabled);
        setFoundPermissionId(foundPermission.id);
      }
    }
  }, [ isSwitchEnabled, permissions, selectedAction, selectedRole, selectedController ]);

  const createPermission = () => {
    setRequestLoading(true);
    client.post('/manage-permissions', {
      type: 'application',
      controller: selectedController,
      action: selectedAction,
      enabled: 1,
      role: selectedRole.id,
    }).then(() => {
      refetchPermissions();
      refetchRoles();
      setEnabled(true);
      alertSuccess('Permission added successfully');
    }).catch((err) => {
      alertError('There was an error');
      console.error(err);
    }).finally(() => {
      setRequestLoading(false);
    });
  };

  const editPermission = (checked) => {
    setRequestLoading(true);
    client.put(`/manage-permissions/${ foundPermissionId }`, { enabled: checked }).then(() => {
      setEnabled(checked);
      refetchPermissions();
      refetchRoles();
      alertSuccess('Permission changed successfully');
    }).catch((err) => {
      alertError('There was an error');
      console.error(err);
    }).finally(() => {
      setRequestLoading(false);
    });
  };

  const createPermissionsForAll = () => {
    const permissionsToCreate = [];
    const permissionsToEdit = [];
    setRequestLoading(true);

    actions.filter((act) => {
      return '__all__' !== act && permissions.find((per) => { return per.controller === selectedController && act === per.action; });
    }).forEach((act) => {
      const foundPermission = permissions.find((per) => {
        return per.action === act && per.controller === selectedController && per?.role?.id === selectedRole?.id;
      });

      if (foundPermission !== undefined && false === foundPermission.enabled) {
        permissionsToEdit.push(foundPermission.id);
      } else if (foundPermission === undefined) {
        permissionsToCreate.push(act);
      }
    });

    Promise.all([
      ...permissionsToCreate.map((per) => {
        return client.post('/manage-permissions', {
          type: 'application',
          controller: selectedController,
          action: per,
          enabled: 1,
          role: selectedRole.id,
        });
      }),
      ...permissionsToEdit.map((per) => {
        return client.put(`/manage-permissions/${ per }`, { enabled: true });
      }),
    ]).then(() => {
      refetchPermissions();
      refetchRoles();
      alertSuccess('Permissions added/changed successfully');
    }).catch((err) => {
      alertError('There was an error');
      console.error(err);
    }).finally(() => {
      setRequestLoading(false);
    });
  };

  if (rolesLoading || permissionsLoading) {
    return (
      <div>
        Loading...
      </div>
    );
  }

  return (
    <div style={ { display: 'flex', gap: '20px' } }>
      <Autocomplete
        options={ roles }
        getOptionLabel={ (option) => { return option?.name ?? ''; } }
        value={ selectedRole }
        style={ { width: 300 } }
        onChange={ (event, newValue) => {
          setSelectedRole(newValue);
        } }
        renderInput={ (params) => {
          return (
            <TextField
              { ...params }
              label='Role'
              variant='outlined'
            />
          );
        } }
      />

      <Autocomplete
        options={ controllers }
        value={ selectedController }
        style={ { width: 300 } }
        freeSolo
        onChange={ (e, newValue) => {
          setSelectedController(newValue);
        } }
        renderInput={ (params) => {
          return (
            <TextField
              { ...params }
              label='Controller'
              variant='outlined'
            />
          );
        } }
      />

      <Autocomplete
        options={ actions }
        value={ selectedAction }
        freeSolo
        style={ { width: 300 } }
        onChange={ (event, newValue) => {
          setSelectedAction(newValue);
        } }
        renderInput={ (params) => {
          return (
            <TextField
              { ...params }
              label='Action'
              variant='outlined'
            />
          );
        } }
      />

      {
        '__all__' === selectedAction ? (
          <Button
            onClick={ createPermissionsForAll }
            variant='contained'
            disabled={ requestLoading }
          >
            Create permissions for all actions
          </Button>
        )
          : (null === enabled) ? (
            <Button onClick={ createPermission } variant='contained' disabled={ requestLoading }>
              Create permission
            </Button>
          ) : (
            <FormControlLabel
              disabled={ !isSwitchEnabled || requestLoading }
              control={ (
                <Switch
                  disabled={ !isSwitchEnabled || requestLoading }
                  checked={ enabled }
                  onChange={ (e) => { editPermission(e.target.checked); } }
                />
              ) }
              label='Enabled'
            />
          )
      }

    </div>
  );
};

export default ChangePermissions;
